import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Loader2, Mail, Phone, Building2, Trash2, Download, Search } from 'lucide-react';
import {
  fetchAdminInquiries,
  updateInquiryStatus,
  deleteInquiry,
  exportInquiriesCsvUrl,
} from '@/lib/api';
import { ADMIN_DASH } from '@/constants/testIds';
import { toast } from 'sonner';

const STATUSES = [
  { slug: 'all', label: 'All' },
  { slug: 'new', label: 'New' },
  { slug: 'contacted', label: 'Contacted' },
  { slug: 'quoted', label: 'Quoted' },
  { slug: 'closed', label: 'Closed' },
];

const STATUS_COLOR = {
  new: 'border-[var(--mw-primary)]/60 text-[var(--mw-primary)]',
  contacted: 'border-amber-400/60 text-amber-300',
  quoted: 'border-sky-400/60 text-sky-300',
  closed: 'border-white/20 text-white/55',
};

export default function InquiriesPanel() {
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');
  const { data, isLoading, mutate } = useSWR(
    ['admin-inquiries', status],
    ([, s]) => fetchAdminInquiries(s),
    { keepPreviousData: true }
  );

  const inquiries = data || [];

  const filtered = useMemo(() => {
    let list = inquiries;
    if (source === 'export') {
      list = list.filter((i) => (i.categories || []).includes('Export'));
    } else if (source === 'wholesale') {
      list = list.filter((i) => !(i.categories || []).includes('Export'));
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (i) =>
          i.business_name?.toLowerCase().includes(q) ||
          i.contact_name?.toLowerCase().includes(q) ||
          i.email?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [inquiries, query, source]);

  const sourceCounts = useMemo(() => {
    const exportN = inquiries.filter((i) => (i.categories || []).includes('Export')).length;
    return {
      all: inquiries.length,
      wholesale: inquiries.length - exportN,
      export: exportN,
    };
  }, [inquiries]);

  const counts = useMemo(() => {
    const c = { all: inquiries.length };
    inquiries.forEach((i) => {
      c[i.status] = (c[i.status] || 0) + 1;
    });
    return c;
  }, [inquiries]);

  const onChangeStatus = async (id, next) => {
    try {
      await updateInquiryStatus(id, next);
      mutate();
      toast.success('Status updated');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await deleteInquiry(id);
      mutate();
      toast.success('Inquiry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const onExport = () => {
    window.open(exportInquiriesCsvUrl(), '_blank');
  };

  return (
    <div data-testid={ADMIN_DASH.inquiriesPanel}>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">
            Wholesale inquiries
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">
            Incoming leads
          </h2>
        </div>
        <button
          type="button"
          onClick={onExport}
          data-testid={ADMIN_DASH.inquiryExport}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {STATUSES.map((s) => {
          const isActive = status === s.slug;
          const count = counts[s.slug] ?? 0;
          return (
            <button
              key={s.slug}
              data-testid={ADMIN_DASH.inquiriesFilter(s.slug)}
              onClick={() => setStatus(s.slug)}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border transition-colors ${
                isActive
                  ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)] text-white'
                  : 'border-white/15 text-white/70 hover:border-white hover:text-white'
              }`}
            >
              {s.label}
              <span className="ml-2 text-white/55">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[10px] uppercase tracking-[0.28em] text-white/40 mr-1">Source</span>
        {[
          { slug: 'all',       label: 'All sources' },
          { slug: 'wholesale', label: 'Wholesale' },
          { slug: 'export',    label: 'Export' },
        ].map((s) => {
          const isActive = source === s.slug;
          const count = sourceCounts[s.slug] ?? 0;
          return (
            <button
              key={s.slug}
              data-testid={ADMIN_DASH.inquiriesSourceFilter(s.slug)}
              onClick={() => setSource(s.slug)}
              className={`text-[11px] uppercase tracking-[0.22em] px-3.5 py-2 border transition-colors ${
                isActive
                  ? 'bg-white text-black border-white'
                  : 'border-white/15 text-white/60 hover:border-white/60 hover:text-white'
              }`}
            >
              {s.label}
              <span className={`ml-2 ${isActive ? 'text-black/45' : 'text-white/40'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="relative mb-8 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          placeholder="Search business, contact or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none pl-9 pr-4 py-2.5 text-sm text-white transition-colors"
        />
      </div>

      {isLoading && inquiries.length === 0 ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-white/40" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 py-20 text-center text-white/50 text-sm uppercase tracking-[0.24em]">
          No inquiries
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filtered.map((q) => (
            <InquiryCard
              key={q.id}
              inquiry={q}
              onChangeStatus={onChangeStatus}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InquiryCard({ inquiry: q, onChangeStatus, onDelete }) {
  const colorClass = STATUS_COLOR[q.status] || STATUS_COLOR.closed;
  return (
    <article
      data-testid={ADMIN_DASH.inquiryRow(q.id)}
      className="border border-white/10 bg-[var(--mw-surface)] p-6 lg:p-7 hover:border-white/25 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/55">
            <Building2 size={14} />
            <span className="truncate">{q.business_name}</span>
          </div>
          <h3 className="font-display text-2xl mt-1">{q.contact_name}</h3>
        </div>
        <select
          value={q.status}
          onChange={(e) => onChangeStatus(q.id, e.target.value)}
          data-testid={ADMIN_DASH.inquiryStatusSelect(q.id)}
          className={`text-[10px] uppercase tracking-[0.22em] bg-[var(--mw-bg)] px-2.5 py-1.5 border outline-none ${colorClass}`}
        >
          <option value="new">new</option>
          <option value="contacted">contacted</option>
          <option value="quoted">quoted</option>
          <option value="closed">closed</option>
        </select>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm text-white/70">
        <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
          <Mail size={14} className="text-[var(--mw-primary)]" />
          <span className="truncate">{q.email}</span>
        </a>
        <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
          <Phone size={14} className="text-[var(--mw-primary)]" /> {q.phone}
        </a>
      </div>

      {q.estimated_volume && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-white/45">
          Volume · <span className="text-white/75">{q.estimated_volume}</span>
        </div>
      )}

      {q.categories?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {q.categories.map((c) => (
            <span
              key={c}
              className="text-[10px] uppercase tracking-[0.22em] border border-white/15 px-2 py-0.5 text-white/70"
            >
              {c}
            </span>
          ))}
        </div>
      )}

      <p className="mt-4 text-sm text-white/75 leading-relaxed border-l border-[var(--mw-primary)]/60 pl-4 whitespace-pre-wrap">
        {q.message}
      </p>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">
          {new Date(q.created_at).toLocaleString()}
        </div>
        <button
          type="button"
          onClick={() => onDelete(q.id)}
          data-testid={ADMIN_DASH.inquiryDelete(q.id)}
          className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[var(--mw-primary)] flex items-center gap-1.5 transition-colors"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </article>
  );
}
